import {
  CircularProgress,
  Divider,
  ListItemText,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import { memo, useEffect, useMemo, useRef, useState } from "react";

import {
  CustomCheckbox,
  InfiniteScroll,
  InputWithDebounce,
  ReuseTooltip,
} from "components/ui";
import { ModalItem } from "../../types/apiTypes";
import { AlertInTriangleIcon } from "../images";
import { AccountPlanStatus } from "../../types/enums";

const AccountsListItem = memo(
  ({ value, name, checked, onClick, children }: any) => (
    <MenuItem sx={{ padding: "4px" }} value={value} onClick={onClick}>
      <CustomCheckbox checked={checked} />
      <Stack flexDirection={"row"} gap={"8px"} flexGrow={1}>
        <ListItemText primary={name} />
        {children}
      </Stack>
    </MenuItem>
  ),
  (prev, next) => prev.checked === next.checked,
);

export const ModalListWithSelect = memo(
  ({
    itemsList,
    withSelectAll,
    withHeader = true,
    headerLeftTitle = "Account",
    headerRightTitle = "Missed",
    canLoadMore = false,
    isLoading = false,
    page,
    loadMore,
    setIsSelectedAll,
    setSelectedHandler,
    setSearchHandler,
    startPage = 0,
  }: {
    itemsList: ModalItem[];
    withSearch?: boolean;
    withSelectAll?: boolean;
    withHeader?: boolean;
    headerLeftTitle?: string;
    headerRightTitle?: string;
    canLoadMore?: boolean;
    isLoading?: boolean;
    page: number;
    loadMore: () => void;
    setIsSelectedAll: (includeAll: boolean) => void;
    setSelectedHandler: (selected: string[]) => void;
    setSearchHandler: (search: string) => void;
    startPage: number;
  }) => {
    const [selected, setSelected] = useState<{ [key: string]: boolean }>({});
    const [allItems, setAllItems] = useState<ModalItem[]>([]);
    const [search, setSearch] = useState("");
    const wrapper = useRef<HTMLDivElement>(null);

    useEffect(() => {
      setSearchHandler(search);
    }, [search]);

    useEffect(() => {
      if (itemsList) {
        setSelected((prev) => {
          const updated = page > 0 ? { ...prev } : {};
          itemsList.forEach(({ id }) => {
            if (!(id in updated)) updated[id] = true;
          });
          return updated;
        });
      }
    }, [itemsList]);

    const selectedIds = useMemo(
      () =>
        Object.entries(selected)
          .filter(([_, value]) => value)
          .map(([key]) => key),
      [selected],
    );

    useEffect(() => {
      setSelectedHandler(selectedIds);
    }, [selectedIds]);

    const isSelectedAll = allItems.length === selectedIds.length;

    useEffect(() => {
      setIsSelectedAll(isSelectedAll);
    }, [isSelectedAll]);

    const selectHandler = (id: string) =>
      setSelected((prev) => ({ ...prev, [id]: !prev[id] }));

    const toggleAllHandler = (checked: boolean) => {
      setSelected((prev) =>
        Object.fromEntries(Object.keys(prev).map((key) => [key, !checked])),
      );
    };

    return (
      <Stack paddingTop="16px">
        <Stack direction="row" gap="8px" pr="4px" mb="12px">
          <InputWithDebounce value={search} setValue={setSearch} />
        </Stack>

        {withHeader && (
          <Stack
            direction="row"
            justifyContent="space-between"
            px="10px"
            pb="4px"
          >
            <Typography variant="body1" color="text.secondary">
              {headerLeftTitle}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {headerRightTitle}
            </Typography>
          </Stack>
        )}

        <InfiniteScroll<ModalItem>
          hasMore={canLoadMore}
          isLoading={isLoading}
          loadMore={loadMore}
          page={page}
          items={itemsList}
          scrollOffset={1200}
          showScrollTopOffset={50}
          startPage={startPage}
          containerRef={wrapper}
          setAllItems={setAllItems}
        >
          {({ items }) => (
            <Stack>
              <Stack ref={wrapper} maxHeight={"220px"} overflow={"auto"}>
                {items.map(({ id, name, value, status }) => (
                  <AccountsListItem
                    key={id}
                    value={id}
                    name={name}
                    checked={selected[id]}
                    onClick={() => selectHandler(id)}
                  >
                    {value && value}
                    {status === AccountPlanStatus.failed && (
                      <Stack justifyContent={"center"} alignItems={"center"}>
                        <ReuseTooltip
                          text={
                            "You’ve already attempted to generate playbook for this prospect."
                          }
                          hideChildren={false}
                          maxWidth={"184px"}
                        >
                          <AlertInTriangleIcon />
                        </ReuseTooltip>
                      </Stack>
                    )}
                  </AccountsListItem>
                ))}

                {isLoading && (
                  <Stack
                    alignItems={"center"}
                    justifyContent={"center"}
                    flexGrow={1}
                    minHeight={"30px"}
                  >
                    <CircularProgress size={20} sx={{ color: "#1C3C6C" }} />
                  </Stack>
                )}

                {items.length === 0 && !isLoading && (
                  <Typography
                    variant="body1"
                    sx={{ color: "#797979", paddingLeft: "12px" }}
                  >
                    No company with such name found
                  </Typography>
                )}
              </Stack>
              {withSelectAll && items.length > 0 && (
                <>
                  <Divider sx={{ color: "#D9D9D9", marginY: "8px" }} />
                  <AccountsListItem
                    name="Select All"
                    checked={isSelectedAll}
                    onClick={() => toggleAllHandler(isSelectedAll)}
                  />
                </>
              )}
            </Stack>
          )}
        </InfiniteScroll>
      </Stack>
    );
  },
  (prev, next) =>
    prev.itemsList === next.itemsList && prev.isLoading === next.isLoading,
);
